import React, { useCallback } from "react";
import { UserPayout } from "@/src/types/user";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";
import { Button } from "@/src/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { convertCurrency } from "@/src/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";
import { EllipsisVertical } from "lucide-react";
import { Badge } from "@/src/components/ui/badge";

interface PayoutTableProps {
  payouts: UserPayout[];
  onPersistPayout: (payout: UserPayout) => void;
  onResendEmail: (payout: UserPayout) => void;
  isPersistingBlocked: boolean;
}

export function PayoutTable({
  payouts,
  onPersistPayout,
  onResendEmail,
  isPersistingBlocked,
}: PayoutTableProps) {
  const shouldHaveActions = useCallback(
    (payout: UserPayout) =>
      (payout.status === "rejected_fraud" ||
        payout.status === "temp_unavailable") &&
      !isPersistingBlocked,
    [isPersistingBlocked],
  );

  const getBadgeVariant = useCallback((status: string) => {
    // variants: "default" | "secondary" | "destructive" | "outline"

    switch (status) {
      case "sent":
        return "default";
      case "rejected_fraud":
        return "destructive";
      case "temp_unavailable":
        return "secondary";
      case "requested":
        return "outline";
      default:
        return "default";
    }
  }, []);

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Payouts</CardTitle>
      </CardHeader>
      <CardContent>
        {payouts.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Value</TableHead>
                <TableHead>Value (EUR)</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Coins</TableHead>
                <TableHead>Option Type</TableHead>
                <TableHead className="w-52 text-center">Mail</TableHead>
                <TableHead className="min-w-36 text-center">Status</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payouts.map((payout: UserPayout, index) => (
                <TableRow key={`payout-${index}`}>
                  <TableCell>
                    {convertCurrency(payout.value, payout.currency)}
                  </TableCell>
                  <TableCell>
                    {convertCurrency(payout.valueEur, "EUR")}
                  </TableCell>
                  <TableCell>{payout.currency}</TableCell>
                  <TableCell>{payout.coins}</TableCell>
                  <TableCell>{payout.optionType}</TableCell>
                  <TableCell className="w-52 truncate text-center">
                    {payout.mail}
                  </TableCell>
                  <TableCell className="items-center justify-center flex">
                    <Badge
                      variant={getBadgeVariant(payout.status)}
                      className="capitalize text-center flex "
                    >
                      {payout.status.split("_").join(" ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(payout.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {shouldHaveActions(payout) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <EllipsisVertical className="size-5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {payout.externalProvider === "tremendous" &&
                            payout.status === "sent" && (
                              <DropdownMenuItem
                                onClick={() => onResendEmail(payout)}
                              >
                                Resend Email
                              </DropdownMenuItem>
                            )}
                          {(payout.status === "rejected_fraud" ||
                            payout.status === "temp_unavailable") &&
                            !isPersistingBlocked && (
                              <DropdownMenuItem
                                onClick={() => onPersistPayout(payout)}
                              >
                                <Button variant="ghost">Persist Payout</Button>
                              </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-4">No payouts found</div>
        )}
      </CardContent>
    </Card>
  );
}
